/* ============================================================================
   DEPTH — magoni.info's cursor effect, per news card
   ----------------------------------------------------------------------------
   magoni.info turns one outlined box with a single line of script:

     main.style.transform = 'rotate3d(10, 180, -6, ' + pageX / -20 + 'deg)'

   on every mousemove, with no perspective. Its border stays on the plane it
   turns on and its text sits 100px in front (translateZ), so as the box
   turns the text slides across the border by 100·sin θ. That slide is the
   whole depth illusion: no vanishing point, no shadow, no glare.

   Here each card is the box. The pill outline is the plane, the photo sits
   behind it (--nr-z-media) and the copy in front (--nr-z-copy), so the two
   slide past the outline in opposite directions. The same rotate3d axis
   (10, 180, −6: mostly Y, with magoni's slight lean and roll) and its sign:
   the cursor moving right turns the card's right side toward you.
   What changed for FIGS:
     • the angle comes from the cursor across the hovered card, centred, and
       stops at ±--nr-tilt-max (magoni's is −pageX/20 across the page, no limit)
     • the card eases toward the cursor and back to flat on leave, closing
       --nr-tilt-follow of the gap per 60fps frame (magoni writes it raw and
       never resets)
     • mouse only (hover: hover, pointer: fine); off under Reduced motion
   The listener sits on the card's link, which never moves, so the rect the
   angle is read from doesn't turn with the card.
   ========================================================================== */

import { state } from '../10-pdp-prototype/mo.js';

const AXIS = '10, 180, -6';
const fine = matchMedia('(hover: hover) and (pointer: fine)');
const css = getComputedStyle(document.documentElement);
const token = (name, fallback) => {
  const v = parseFloat(css.getPropertyValue(name));
  return Number.isFinite(v) ? v : fallback;
};
const MAX = token('--nr-tilt-max', 18);
const FOLLOW = token('--nr-tilt-follow', 0.12);

const cards = new WeakMap(); // link → { stage, target, angle }
const moving = new Set();
let raf = 0;
let last = 0;

const write = (c) => {
  c.stage.style.transform = c.angle === 0 ? '' : `rotate3d(${AXIS}, ${c.angle.toFixed(3)}deg)`;
};

function frame(now) {
  /* frame-rate independent, and the lab bar's Speed slows it like every
     other animation on the page */
  const dt = Math.min(64, now - (last || now)) / (1000 / 60);
  last = now;
  const k = 1 - Math.pow(1 - FOLLOW, dt * state.speed);
  moving.forEach((c) => {
    c.angle += (c.target - c.angle) * k;
    if (Math.abs(c.target - c.angle) < 0.01) {
      c.angle = c.target;
      moving.delete(c);
    }
    write(c);
  });
  raf = moving.size ? requestAnimationFrame(frame) : 0;
  if (!raf) last = 0;
}

function aim(c, target) {
  c.target = target;
  moving.add(c);
  if (!raf) raf = requestAnimationFrame(frame);
}

/* Bind the cards inside `root` that aren't bound yet (Load More adds more). */
export function armDepth(root) {
  root.querySelectorAll('[data-news-link]').forEach((link) => {
    if (cards.has(link)) return;
    const c = { stage: link.querySelector('[data-news-stage]'), target: 0, angle: 0 };
    cards.set(link, c);
    link.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !fine.matches || state.reduced) return;
      const r = link.getBoundingClientRect();
      const nx = Math.max(-0.5, Math.min(0.5, (e.clientX - r.left) / r.width - 0.5));
      aim(c, -nx * 2 * MAX);
    });
    link.addEventListener('pointerleave', () => aim(c, 0));
  });
}

/* Flatten every card at once (Reduced motion turned on, Replay). */
export function settleDepth(root) {
  root.querySelectorAll('[data-news-link]').forEach((link) => {
    const c = cards.get(link);
    if (!c) return;
    c.target = 0;
    c.angle = 0;
    moving.delete(c);
    write(c);
  });
}
